import purchaseInvalidationApi from '../../api/purchase/purchaseInvalidationApi';
import { PurchaseInvalidationDTO } from '../../types/purchases/invalidation/PurchaseInvalidationDTO';

/**
 * 🧾 Servicio de Anulación de Compras
 *
 * Encapsula las operaciones relacionadas con:
 * - Registro de anulaciones de compras
 * - Obtención de datos para precargar el formulario
 *
 * Todas las funciones retornan directamente `response.data`
 * para simplificar el consumo en hooks o componentes.
 */
const PurchaseInvalidationService = {

    /**
     * 📤 Registrar anulación de compra
     *
     * Envía al backend la información necesaria para invalidar
     * un documento de compra.
     *
     * 🔗 Endpoint:
     * POST /api/purchases/invalidation
     *
     * @param payload - Datos de la anulación (DTO del formulario)
     * @returns Respuesta del backend (status, message, etc.)
     *
     * ⚠️ Reglas importantes:
     * - `generationCodeToInvalidate` es obligatorio
     * - `replacementGenerationCode` es obligatorio solo para tipos 1 y 3
     */
    register: async (payload: PurchaseInvalidationDTO) => {
        const response = await purchaseInvalidationApi.add(payload);
        return response.data;
    },

    /**
     * 📥 Obtener datos para precargar formulario
     *
     * Consulta al backend la información base de una compra
     * para facilitar el proceso de anulación (prefill).
     *
     * 🔗 Endpoint:
     * GET /api/purchases/invalidation/prefill/{purchaseId}
     *
     * @param purchaseId - Identificador de la compra
     * @returns Datos necesarios para inicializar el formulario
     *
     * 📌 Incluye típicamente:
     * - documentId
     * - generationCode
     * - documentTypeCode
     */
    getPrefill: async (purchaseId: number) => {
        const response = await purchaseInvalidationApi.fetchCustom(`/prefill/${purchaseId}`);
        return response.data;
    },
};

export default PurchaseInvalidationService;